import React from "react";
import moment from "moment";
import styled from "styled-components";
import { tokens } from "../../../../../public/tokens";
import Button from "../../../../library/Button";
import { StudentAvatar } from "./PMCGradeBook.styles";

const { colors, typography, globals } = tokens;

const SUMMARY_COUNTERS = [
    { key: "programmes", icon: "books", label: "Programmes" },
    { key: "modules", icon: "book", label: "Modules" },
    { key: "students", icon: "users", label: "Students" },
    { key: "graduates", icon: "graduation-cap", label: "Graduates" },
    { key: "lockedProgrammes", icon: "lock", label: "Locked Programmes" },
];

const VERSION_STATUS_STYLES = {
    active: { background: colors.states.success["100"], color: colors.states.success["500"] },
    inactive: { background: "rgba(108, 117, 125, 0.1)", color: colors.neutral.grey["600"] },
    locked: { background: colors.states.warning["500"], color: colors.neutral.white },
    unlocked: { background: "rgba(108, 117, 125, 0.1)", color: colors.neutral.grey["600"] },
};

const VersionStatusBadge = styled.span(({ $colors }) => ({
    minHeight: "22px",
    maxHeight: "22px",
    padding: `${globals["spacing-1"]} ${globals["spacing-2"]}`,
    borderRadius: globals["border-radius"],
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: globals["spacing-1"],
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["md-12px"],
    fontWeight: typography.fontWeights.semibold,
    lineHeight: 1,
    whiteSpace: "nowrap",
    ...$colors,

    i: {
        width: globals["spacing-2-5"],
        fontSize: typography.fontSize["sm-10px"],
        lineHeight: 1,
        textAlign: "center",
        flexShrink: 0,
    },
}));

const StyledSummaryStrip = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: globals["spacing-4"],
    width: "100%",
    marginBottom: globals["spacing-2"],
    padding: globals["spacing-3"],
    background: colors.neutral.white,
    border: `1px solid ${colors.surfaces["border-divider"]}`,
    borderRadius: globals["border-radius"],
});

const SummaryStripMain = styled.div({
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: globals["spacing-4"],
    minWidth: 0,
});

const SummaryStripDivider = styled.span({
    width: "1px",
    height: "35px",
    flex: "0 0 1px",
    background: colors.surfaces["border-divider"],
});

const VersionInfo = styled.div({
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-2-5"],
    flexShrink: 0,
});

const VersionName = styled.span({
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["xl-18px"],
    fontWeight: typography.fontWeights.bold,
    color: colors.neutral.grey["900"],
    whiteSpace: "nowrap",
});

const QCInfo = styled.div({
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-1-5"],
    flexShrink: 0,
});

const QCDetails = styled.span({
    display: "flex",
    flexDirection: "column",
    gap: globals["spacing-0-5"],
});

const QCName = styled.span({
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["md-12px"],
    fontWeight: typography.fontWeights.semibold,
    color: colors.text.primary,
    whiteSpace: "nowrap",
});

const QCRole = styled.span({
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["sm-10px"],
    color: colors.text.secondary,
    whiteSpace: "nowrap",
});

const NotAssignedIcon = styled.span({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    background: "rgba(227, 146, 25, 0.1)",
    color: colors.states.warning["500"],
    fontSize: typography.fontSize["lg-14px"],
});

const NotAssignedText = styled.span({
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["md-12px"],
    color: colors.text.secondary,
    whiteSpace: "nowrap",
});

const AlertDot = styled.i({
    marginLeft: globals["spacing-1"],
    color: colors.ui.destructive,
    fontSize: "10px",
    lineHeight: 1,
});

const SummaryCounters = styled.div({
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: globals["spacing-5"],
});

const SummaryCounter = styled.div({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
});

const SummaryCounterLabel = styled.span({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-1-5"],
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["md-12px"],
    fontWeight: typography.fontWeights.semibold,
    color: colors.text.primary,
    whiteSpace: "nowrap",

    i: {
        width: globals["spacing-3-5"],
        fontSize: typography.fontSize["lg-14px"],
        textAlign: "center",
    },
});

const SummaryCounterValue = styled.span({
    paddingLeft: globals["spacing-5"],
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["md-12px"],
    color: colors.text.secondary,
    whiteSpace: "nowrap",
});

const SummaryStripActions = styled.div({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: globals["spacing-1-5"],
    flexShrink: 0,
});

const SummaryStripButtons = styled.div({
    display: "flex",
    alignItems: "center",
    gap: globals["spacing-2"],
});

const SummaryStripUpdatedRow = styled.div({
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    marginBottom: globals["spacing-2"],
});

const SummaryStripUpdatedText = styled.small({
    display: "inline-flex",
    alignItems: "center",
    gap: globals["spacing-1"],
    fontFamily: globals["font-family"],
    fontSize: typography.fontSize["sm-10px"],
    color: colors.text.secondary,
    whiteSpace: "nowrap",
});

/**
 * PMC Gradebook's "Version Summary Strip": version name, Active/Locked badges, the
 * read-only Qualification Co-ordinator, summary counters, the Calculate Final Grades /
 * Download SOR actions, and the last-update line.
 */
function SummaryStrip({
    versionName = "—",
    isActiveVersion = false,
    isVersionLocked = false,
    coordinator,
    totals,
    isLoading = false,
    isCalculating = false,
    isBusy = false,
    onCalculateFinalGrades,
    onDownloadSOR,
    downloadDisabled = false,
    updatedAt,
    updatedByName,
}) {
    return (
        <>
            <StyledSummaryStrip aria-label="Programme summary">
                <SummaryStripMain>
                    <VersionInfo>
                        <VersionName>{versionName}</VersionName>

                        <VersionStatusBadge $colors={VERSION_STATUS_STYLES[isActiveVersion ? "active" : "inactive"]}>
                            <i className={`fal fa-${isActiveVersion ? "check-circle" : "times-circle"}`} aria-hidden="true" />
                            {isActiveVersion ? "Active" : "Inactive"}
                        </VersionStatusBadge>

                        <VersionStatusBadge $colors={VERSION_STATUS_STYLES[isVersionLocked ? "locked" : "unlocked"]}>
                            <i className={`fal fa-${isVersionLocked ? "lock" : "unlock"}`} aria-hidden="true" />
                            {isVersionLocked ? "Locked" : "Unlocked"}
                        </VersionStatusBadge>
                    </VersionInfo>

                    <SummaryStripDivider aria-hidden="true" />

                    <QCInfo>
                        {coordinator ? (
                            <>
                                <StudentAvatar>
                                    {coordinator.avatar ? (
                                        <img src={coordinator.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    ) : (
                                        <i className="fal fa-user" aria-hidden="true" />
                                    )}
                                </StudentAvatar>

                                <QCDetails>
                                    <QCName>{coordinator.name}</QCName>
                                    <QCRole>Qualification Co-ordinator</QCRole>
                                </QCDetails>
                            </>
                        ) : (
                            <>
                                <NotAssignedIcon aria-hidden="true">
                                    <i className="fal fa-user" aria-hidden="true" />
                                </NotAssignedIcon>

                                <NotAssignedText>
                                    Not Assigned
                                    <AlertDot className="fas fa-exclamation-circle" aria-hidden="true" />
                                </NotAssignedText>
                            </>
                        )}
                    </QCInfo>

                    <SummaryStripDivider aria-hidden="true" />

                    <SummaryCounters>
                        {SUMMARY_COUNTERS.map((counter) => (
                            <SummaryCounter key={counter.key}>
                                <SummaryCounterLabel>
                                    <i className={`fal fa-${counter.icon}`} aria-hidden="true" />
                                    {counter.label}
                                </SummaryCounterLabel>
                                <SummaryCounterValue>
                                    {isLoading ? "…" : totals?.[counter.key] ?? 0}
                                </SummaryCounterValue>
                            </SummaryCounter>
                        ))}
                    </SummaryCounters>
                </SummaryStripMain>

                <SummaryStripActions>
                    <SummaryStripButtons>
                        <Button
                            variant="accent"
                            size="lg"
                            type="button"
                            title={isCalculating ? "Calculation is running" : isBusy ? "A calculation is already running" : "Calculate Final Grades"}
                            disabled={isBusy}
                            onClick={onCalculateFinalGrades}
                        >
                            <i
                                className={isCalculating ? "fal fa-circle-notch fa-spin" : "fal fa-sync"}
                                style={isCalculating ? { color: tokens.colors.brand.blue["700"] } : undefined}
                            ></i>
                            {isCalculating ? "Calculating Grades..." : "Calculate Final Grades"}
                        </Button>
                        <Button
                            variant="primary"
                            size="lg"
                            type="button"
                            title={isBusy ? "A calculation is already running" : "Download Statement of Results"}
                            disabled={isBusy || downloadDisabled}
                            onClick={onDownloadSOR}
                        >
                            <i className="fal fa-download"></i>
                            Download SOR
                        </Button>
                    </SummaryStripButtons>
                </SummaryStripActions>
            </StyledSummaryStrip>

            {updatedAt && updatedByName && (
                <SummaryStripUpdatedRow>
                    <SummaryStripUpdatedText>
                        <i className="fal fa-history" aria-hidden="true" />
                        Updated <strong>{moment(updatedAt).format('DD MMM YY')}</strong>{" "}
                        at <strong>{moment(updatedAt).format('HH:mm')}</strong>{" "}
                        by <strong>{updatedByName}</strong>
                    </SummaryStripUpdatedText>
                </SummaryStripUpdatedRow>
            )}
        </>
    );
}

export default SummaryStrip;
